import { t } from "i18next";
import { get, isNil } from "lodash";

import {
  ComputedDataKey,
  DEFAULT_COLUMN_WIDTH,
  FIELD_TYPE_IDS,
  FILTERABLE_FILTER_BASIC_FIELDS,
  FORM_VISIBLE_CONDITION_UNDEFINED_TOKEN,
  METADATA_KEY,
  METADATA_KEY_AND_DOT,
  type FILTER_FIELD_VALUE_ID,
  type FORM_FIELD_KEYS,
  type TGridFieldKeys,
} from "../constants";
import type { IExtendableDto, PermissionMap } from "../dto";
import { mapTableColumnWidth } from "../mappers";
import type {
  EditFormTemplateType,
  GridTemplateType,
  ICustomField,
  IDropdownItem,
  IDynamicEditFormRoot,
  IEditFormTabContainer,
  IFormSchema,
  ObjectLiteral,
  RydeUiUnit,
} from "../models";
import { CustomGuard } from "./guard.validation.helper";
import { Log } from "./log.helpers";
import { hasKeys } from "./objects.helpers";
import { parseBoolean } from "./string.helpers";

const getFormVisibleConditionAndMap = (
  formVisibleCondition: Record<string, unknown>,
): object => {
  for (const value of Object.values(formVisibleCondition)) {
    if (
      value &&
      typeof value === "object" &&
      "$nin" in value &&
      Array.isArray(value.$nin)
    ) {
      for (let i = 0; i < value.$nin.length; i++) {
        if (value.$nin[i] === FORM_VISIBLE_CONDITION_UNDEFINED_TOKEN) {
          value.$nin[i] = undefined;
        }
      }
    }
  }

  return formVisibleCondition;
};

export const getDynamicEditFormRootAndMap = (
  schemeData: IDynamicEditFormRoot,
): IDynamicEditFormRoot => {
  const controls = schemeData.controls;

  for (const control of controls) {
    if (control.formVisibleCondition) {
      control.formVisibleCondition = getFormVisibleConditionAndMap(
        control.formVisibleCondition,
      );
    }
  }

  return schemeData;
};

export const getValidatedFormControls = (
  controls: ICustomField[],
  formSchema: IFormSchema | undefined,
  bulkControlNames?: string[],
): ICustomField[] => {
  const controlSet = new Set(controls);
  const fieldIdsUnique = Array.from(
    new Set(formSchema?.layout?.map(tab => tab.fieldIds).flat()),
  );

  const bulkControlNamesSet = bulkControlNames
    ? new Set(bulkControlNames)
    : null;
  const fieldIdsUniqueSet = new Set(fieldIdsUnique);
  const curControls: ICustomField[] = [];
  controlSet?.forEach(control => {
    const isBulkCheckedControl = bulkControlNamesSet?.has(
      controlNameMetadataResolve(control),
    );

    if (
      control?.formValidation?.type &&
      control.formValidation?.rules?.length &&
      fieldIdsUniqueSet.has(control.id) &&
      (!bulkControlNames ||
        (isBulkCheckedControl && !control?.formAttributes?.notBulkEditable))
    ) {
      curControls.push(control);
    }
  });

  return curControls;
};

export const getDynamicFormControls = (
  controls: ICustomField[],
  tabs?: IEditFormTabContainer[],
  tabIndex = 0,
): ICustomField[] => {
  if (!tabs?.length) {
    return controls;
  }

  if (!tabs[tabIndex]) {
    Log.warn(t("errors.invalidTabIndex", { tabIndex: tabIndex }), tabs);
    return [];
  }

  const { fieldIds } = tabs[tabIndex];

  const curControls: ICustomField[] = [];
  fieldIds?.forEach(controlId => {
    const curControl = controls.find(control => control.id === controlId);

    if (curControl?.formFieldTypeId) {
      curControls.push(curControl);
    }
  });
  return curControls;
};

/**
 * Bulk-editable controls in **form schema layout order** (tab order, then `fieldIds`
 * order within each tab). Controls that are bulk-editable but absent from the layout
 * (e.g. grid-only columns) are appended at the end, preserving their `controls` order.
 * Without a layout, preserves the order of `controls` from the schema.
 */
export const getDynamicFormBulkUpdateControls = (
  controls: ICustomField[],
  formSchema?: IFormSchema,
): ICustomField[] => {
  const filtered = controls.filter(
    control =>
      control.formFieldTypeId && !control.formAttributes?.notBulkEditable,
  );

  const layoutFieldIds = [
    ...new Set(formSchema?.layout?.flatMap(tab => tab.fieldIds ?? []) ?? []),
  ];

  if (!layoutFieldIds.length) {
    return filtered;
  }

  const controlMap = new Map(filtered.map(control => [control.id, control]));

  const layoutOrdered = layoutFieldIds
    .map(id => controlMap.get(id))
    .filter((control): control is ICustomField => Boolean(control));

  const layoutIdsSet = new Set<number | undefined>(layoutFieldIds);
  const nonLayout = filtered.filter(control => !layoutIdsSet.has(control.id));

  return [...layoutOrdered, ...nonLayout];
};

/**
 * Values a bulk-updated dropdown may offer when its items are filtered by an array on the ancestor item
 * (e.g. a booking's meal is limited to its shift's `mealIds`).
 *
 * The bulk form has no ancestor field to watch — the ancestor lives on each selected record — so the
 * allowed set is the **intersection** across all selected rows: only an option every selected record
 * accepts may be applied to them in one go. A row without an ancestor value, or whose ancestor is
 * unknown/offers nothing, empties the intersection and leaves the field with no valid option.
 *
 * Only rows already fetched into the grid are visible here, so a select-all spanning unfetched pages
 * yields the intersection of the loaded ones; the server re-checks and reports what it had to skip.
 */
export const getBulkAncestorAllowedValues = ({
  selectedRowsValues,
  ancestorItems,
  ancestorFieldName,
  matchAncestorItemArrayKey,
}: {
  selectedRowsValues: IExtendableDto[] | undefined;
  ancestorItems: IDropdownItem[] | undefined;
  ancestorFieldName: string;
  matchAncestorItemArrayKey: string;
}): (string | number)[] => {
  if (!selectedRowsValues?.length || !ancestorItems) {
    return [];
  }

  const ancestorItemByValue = new Map(
    ancestorItems.map(item => [String(item.value), item]),
  );

  let allowedValues: string[] | undefined;

  for (const row of selectedRowsValues) {
    const ancestorValue = get(row, ancestorFieldName);
    const ancestorItem = isNil(ancestorValue)
      ? undefined
      : ancestorItemByValue.get(String(ancestorValue));
    const rowValues = (ancestorItem as ObjectLiteral | undefined)?.[
      matchAncestorItemArrayKey
    ];

    if (!Array.isArray(rowValues)) {
      return [];
    }

    const rowValuesSet = new Set(rowValues.map(String));

    allowedValues = allowedValues
      ? allowedValues.filter(value => rowValuesSet.has(value))
      : [...rowValuesSet];

    if (!allowedValues.length) {
      return [];
    }
  }

  return allowedValues ?? [];
};

export const normalizeMultiselectValueToArray = (
  value: unknown,
): (string | number)[] => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(
      entry => typeof entry === "string" || typeof entry === "number",
    );
  }

  if (typeof value === "string" || typeof value === "number") {
    return [value];
  }

  return [];
};

/**
 * Union of multiselect values already assigned on the selected bulk rows (e.g. every
 * passenger's shiftIds). RYDE-7753: REMOVE prefill and inactive/orphan dropdown options.
 * Uses already-loaded selectedRowsValues — not fetched on bulk form open.
 */
export const getBulkMultiselectAssignedValuesUnion = ({
  selectedRowsValues,
  fieldName,
}: {
  selectedRowsValues: IExtendableDto[] | undefined;
  fieldName: string;
}): (string | number)[] => {
  if (!selectedRowsValues?.length) {
    return [];
  }

  const union = new Set<string>();

  for (const row of selectedRowsValues) {
    for (const value of normalizeMultiselectValueToArray(get(row, fieldName))) {
      union.add(String(value));
    }
  }

  return [...union].map(value => {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) && String(numericValue) === value
      ? numericValue
      : value;
  });
};

/**
 * Addition (`addition_value`) custom fields in **form tab / schema encounter order** (not sorted).
 * Omits fields marked `notBulkEditable`. Without tabs, preserves the order of `controls` from the schema.
 */
export const collectAdditionValueBulkControlsPreservingTabOrder = ({
  controls,
  tabs,
}: {
  controls: ICustomField[];
  tabs?: IEditFormTabContainer[];
}): ICustomField[] => {
  const isEligibleAddition = (control: ICustomField): boolean =>
    control.formFieldTypeId === FIELD_TYPE_IDS.FORM.addition_value &&
    !control.formAttributes?.notBulkEditable;

  if (!tabs?.length) {
    return controls.filter(isEligibleAddition);
  }

  const fieldIdsPreservingTabOrder = [
    ...new Set(tabs.flatMap(tab => tab.fieldIds ?? [])),
  ];

  const controlMap = new Map(controls.map(c => [c.id, c]));

  return fieldIdsPreservingTabOrder
    .map(id => controlMap.get(id))
    .filter((c): c is ICustomField => Boolean(c && isEligibleAddition(c)));
};

/** Grid/form field visibility by `readPermission` (same rules as `useHasPermissionCustomField`). */
export const isCustomFieldReadableByPermission = ({
  control,
  permissions,
  entityIdToName,
}: {
  control: ICustomField;
  permissions: PermissionMap | undefined;
  entityIdToName: Map<number, RydeUiUnit>;
}): boolean => {
  const readPermission = control.readPermission;

  if (!readPermission?.action || !readPermission?.entityId) {
    return true;
  }

  const entityName = entityIdToName.get(readPermission.entityId);

  if (!entityName) {
    return false;
  }

  return (
    permissions?.[entityName]?.some(act => act === readPermission.action) ??
    false
  );
};

export const getDynamicGridControls = (
  controls: ICustomField[],
  gridColumns: number[],
): ICustomField[] => {
  if (!gridColumns?.length) {
    return [];
  }

  const curControls: ICustomField[] = [];
  gridColumns.forEach(gridControlId => {
    const curControl = controls.find(control => control.id === gridControlId);

    if (curControl?.gridFieldTypeId) {
      curControls.push(curControl);
    }
  });

  return curControls;
};

export const getDynamicFilters = (
  controls: ICustomField[],
  filterColumns: number[],
): ICustomField[] => {
  if (!filterColumns?.length) {
    return [];
  }

  const curFilters: ICustomField[] = [];
  filterColumns.forEach(gridControlId => {
    const curControl = controls.find(control => control.id === gridControlId);

    if (curControl?.gridFieldTypeId) {
      curFilters.push(curControl);
    }
  });

  return curFilters;
};

export const getComputedGridFieldName = (field: ICustomField): string => {
  return field.gridAttributes?.fieldNameGrid?.length
    ? field.gridAttributes?.fieldNameGrid
    : fieldNameMetadataResolve(field.name, field.isColumn);
};

const FormFieldTemplateNamesById = (() => {
  const result: Record<number, EditFormTemplateType> = {};
  Object.keys(FIELD_TYPE_IDS.FORM).forEach(
    name =>
      (result[FIELD_TYPE_IDS.FORM[name as FORM_FIELD_KEYS]] =
        name as EditFormTemplateType),
  );

  return result;
})();

const GridFieldTemplateNamesById = (() => {
  const result: Record<number, GridTemplateType> = {};
  Object.keys(FIELD_TYPE_IDS.GRID).forEach(
    name =>
      (result[FIELD_TYPE_IDS.GRID[name as TGridFieldKeys]] =
        name as GridTemplateType),
  );

  return result;
})();

export const getFormFieldTypeNameById = (
  id: number,
): EditFormTemplateType | undefined => {
  return FormFieldTemplateNamesById[id];
};

export const getGridFieldTypeNameById = (
  id: number,
): GridTemplateType | undefined => {
  return GridFieldTemplateNamesById[id];
};

export const getGridFieldColumnWidthById = (
  gridFieldTypeId: number | undefined,
): number => {
  if (!gridFieldTypeId) {
    return DEFAULT_COLUMN_WIDTH;
  }

  const controlInfoName = getGridFieldTypeNameById(gridFieldTypeId);

  if (!controlInfoName) {
    return DEFAULT_COLUMN_WIDTH;
  }

  const width = mapTableColumnWidth[controlInfoName];
  return width || DEFAULT_COLUMN_WIDTH;
};

export const formValueOrDefault = <T>(
  val: T | null | undefined,
  defaultValue: T | null | undefined,
  fallbackValue: T | null | undefined = undefined,
): T | null | undefined => {
  if (defaultValue === null || defaultValue === undefined) {
    return val;
  }

  if (val === null || val === undefined) {
    return defaultValue || fallbackValue;
  }

  return val;
};

export const isCustomFieldFilterCompatible = (field: ICustomField): boolean => {
  if (!field) {
    return false;
  }

  const isMultiselectBound =
    field.filterFieldTypeId === FIELD_TYPE_IDS.FILTER.multiselect_bound &&
    field.formAttributes?.dropItemsSource;
  const isMultiselect =
    field.filterFieldTypeId === FIELD_TYPE_IDS.FILTER.multiselect &&
    field.formAttributes?.selectItems?.length;

  const isMachWithBasicFilter = FILTERABLE_FILTER_BASIC_FIELDS.has(
    field.filterFieldTypeId as FILTER_FIELD_VALUE_ID,
  );

  if (isMultiselectBound || isMultiselect || isMachWithBasicFilter) {
    return true;
  }

  return false;
};

export const isCustomFieldSearchable = (field: ICustomField): boolean => {
  return Boolean(field?.filterSearchable);
};

export const extractSearchableControlName = (control: ICustomField): string => {
  return (
    control.gridAttributes?.fieldNameGrid ||
    fieldNameMetadataResolve(control.name, control.isColumn)
  );
};

/** A phone however it is configured: a phone input on the form or a phone cell in the grid. */
export const isPhoneCustomField = (control: ICustomField): boolean =>
  control.formFieldTypeId === FIELD_TYPE_IDS.FORM.phone ||
  control.gridFieldTypeId === FIELD_TYPE_IDS.GRID.phone;

/** The searchable phone fields, which client search also matches by their local form ("052..."). */
export const getSearchablePhoneFieldNames = (
  controls: ICustomField[] | undefined,
): string[] =>
  (controls ?? [])
    .filter(
      control =>
        isCustomFieldSearchable(control) && isPhoneCustomField(control),
    )
    .map(extractSearchableControlName);

export const controlNameMetadataResolve = (control: ICustomField): string =>
  parseBoolean(control?.isColumn)
    ? control.name
    : appendMetadataPrefix(control.name);

export const fieldNameMetadataResolve = (
  fieldName: string,
  isColumn: boolean | string | undefined,
): string => {
  return parseBoolean(isColumn) || fieldName.startsWith(ComputedDataKey)
    ? fieldName
    : appendMetadataPrefix(fieldName);
};

export const isMetadataField = (
  fieldName: string | null | undefined,
): boolean => fieldName?.startsWith(METADATA_KEY_AND_DOT) || false;

export const appendMetadataPrefix = (fieldName: string): string => {
  if (isMetadataField(fieldName)) {
    return fieldName;
  }

  return `${METADATA_KEY}.${fieldName}`;
};

export const removeMetadataPrefix = (fieldName: string): string => {
  if (isMetadataField(fieldName)) {
    return fieldName.replace(METADATA_KEY_AND_DOT, "");
  }

  return fieldName || "";
};

interface IGetResolvedDuplicatedDtoArguments {
  dto: object;
  prohibitedToDuplicateFields: Set<string>;
}

interface IGetProhibitedToDuplicateFieldsArguments {
  dto: unknown;
  controls: ICustomField[];
}

export const getProhibitedToDuplicateFields = ({
  dto,
  controls,
}: IGetProhibitedToDuplicateFieldsArguments): Set<string> => {
  if (!controls?.length) {
    return new Set<string>();
  }

  return controls.reduce((acc, control) => {
    const excludeOnDuplicate = control?.formAttributes?.excludeOnDuplicate;
    const fieldPath = controlNameMetadataResolve(control);

    if (!excludeOnDuplicate) {
      return acc;
    }

    if (excludeOnDuplicate === true) {
      acc.add(fieldPath);
      return acc;
    }

    if (typeof excludeOnDuplicate === "object") {
      const conditionEvaluator = CustomGuard(excludeOnDuplicate);

      // If condition can't be parsed, default to excluding to avoid duplicating sensitive fields.
      if (!conditionEvaluator?.length) {
        acc.add(fieldPath);
        return acc;
      }

      const shouldExclude = conditionEvaluator(dto as Record<string, unknown>);
      if (shouldExclude) {
        acc.add(fieldPath);
      }
    }

    return acc;
  }, new Set<string>());
};

const setByPath = ({
  obj,
  path,
  value,
}: {
  obj: ObjectLiteral;
  path: string;
  value: unknown;
}): void => {
  const parts = path.split(".");
  const lastKey = parts.pop();

  if (!lastKey) {
    return;
  }

  const target = parts.reduce<ObjectLiteral>((acc, key) => {
    const existing = acc[key];
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      acc[key] = {};
    } else {
      acc[key] = { ...(existing as ObjectLiteral) };
    }
    return acc[key] as ObjectLiteral;
  }, obj);

  target[lastKey] = value;
};

export const getResolvedDuplicatedDto = ({
  dto,
  prohibitedToDuplicateFields,
}: IGetResolvedDuplicatedDtoArguments): ObjectLiteral => {
  const result: ObjectLiteral = { ...(dto as ObjectLiteral) };

  if (!dto) {
    return {};
  }

  prohibitedToDuplicateFields.forEach(fieldPath => {
    setByPath({ obj: result, path: fieldPath, value: null });
  });

  return result;
};

export const isMaxItemsReached = (
  maxItemsToAdd: number | undefined,
  index: number,
): boolean => {
  return !!(maxItemsToAdd && index === maxItemsToAdd);
};

export const getComputedFormFieldName = (f: ICustomField): string => {
  return f.formAttributes?.fieldNameForm?.length
    ? f.formAttributes?.fieldNameForm
    : fieldNameMetadataResolve(f.name, f.isColumn);
};

export const shouldResetOnEditOrDuplicate = ({
  dto,
  isEdit,
  isDuplicate,
}: {
  dto: IExtendableDto | undefined;
  isEdit: boolean | undefined;
  isDuplicate: boolean | undefined;
}): boolean => {
  const condition = dto && ((isEdit && dto.id) || (isDuplicate && !dto.id));
  return Boolean(condition);
};

export const shouldResetOnAddNew = ({
  dto,
  isEdit,
  isDuplicate,
}: {
  dto: IExtendableDto | undefined;
  isEdit: boolean | undefined;
  isDuplicate: boolean | undefined;
}): boolean => {
  const condition = dto && !isEdit && !isDuplicate && hasKeys(dto);
  return Boolean(condition);
};

export const iterateFormFields = (
  formValuesObject: IExtendableDto,
): string[] => {
  const metadata = formValuesObject?.metadata;

  let result: string[] = [];

  Object.keys(formValuesObject).forEach(key => {
    if (key === METADATA_KEY && typeof metadata === "object") {
      result = [
        ...result,
        ...Object.keys(metadata).map(
          metadataPropertyKey => `${METADATA_KEY}.${metadataPropertyKey}`,
        ),
      ];
    } else {
      result.push(key);
    }
  });

  return result;
};
